import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {number | null} */
  #previousVariantIndex = null;
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the matching slide with animation,
   * avoiding full re-render for smoother UX.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail.resource;
    const mediaId = variant?.featured_media?.id;
    const slideshow = this.slideshow;
    if (!slideshow || !mediaId) return;

    const { slides = [] } = slideshow.refs;
    const nextIndex = slides.findIndex((s) => s.getAttribute('slide-id') === String(mediaId));
    if (nextIndex < 0) return;

    // Determine direction
    const direction = this.#previousVariantIndex != null && nextIndex > this.#previousVariantIndex ? 'right' : 'left';
    this.#previousVariantIndex = nextIndex;

    // Add transient direction class for CSS animation
    const root = slideshow;
    root.classList.remove('slideshow--animating-left', 'slideshow--animating-right');
    root.classList.add(direction === 'right' ? 'slideshow--animating-right' : 'slideshow--animating-left');

    // Select the target slide
    slideshow.select({ index: nextIndex }, undefined, { animate: true });

    // Cleanup animation hint after a short delay
    setTimeout(() => {
      root.classList.remove('slideshow--animating-left', 'slideshow--animating-right');
    }, 350);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
